import { pickPreferredFuel, type DataSourceKind, type RankedValue } from "./dataSource";
import { ofpTripFuelGal } from "./fuelBurn";
import type { FlightLogEntry, Mission, MissionOfpAttachment } from "./types";

export type TripFuelResolveInput = {
  /** Arrival / post-flight user entry, already converted to US gal. */
  userArrivalGal?: number | null;
  /** SimConnect start−end or integrated burn, US gal. */
  simUsedGal?: number | null;
  /** SimBrief OFP planned burn, US gal. */
  ofpPlannedGal?: number | null;
  /** Generator / ticket `mission.fuelGal`. */
  deskEstimateGal?: number | null;
};

/**
 * SimBrief trip fuel in US gallons for the burn compare.
 * Block, ramp, and takeoff fuel are not used when trip fuel is missing.
 */
export function ofpPlannedFuelGal(
  ofp?: Pick<MissionOfpAttachment, "plannedTripGal" | "plannedFuelGal" | "fuelPlan" | "aircraftType" | "plannedTripLbs"> | null,
): number | null {
  return ofpTripFuelGal(ofp);
}

export function arrivalFuelUsedGal(log?: Pick<FlightLogEntry, "arrival"> | null): number | null {
  const gal = log?.arrival?.fuelUsedGal;
  return typeof gal === "number" && Number.isFinite(gal) && gal > 0 ? gal : null;
}

export function deskFuelEstimateGal(mission?: Pick<Mission, "fuelGal"> | null): number | null {
  const gal = mission?.fuelGal;
  return typeof gal === "number" && Number.isFinite(gal) && gal > 0 ? gal : null;
}

/**
 * Single burn figure for cost / ledger. Precedence: user → sim → OFP → desk.
 * Analytics still plots planned vs actual side-by-side when both exist.
 */
export function resolveTripFuelGal(input: TripFuelResolveInput): RankedValue<number> | null {
  return pickPreferredFuel({
    userOverride: input.userArrivalGal,
    simActual: input.simUsedGal,
    ofpPlanned: input.ofpPlannedGal,
    deskEstimate: input.deskEstimateGal,
  });
}

export function resolveTripFuelGalForMission(input: {
  mission?: Pick<Mission, "fuelGal" | "ofpAttachment"> | null;
  log?: Pick<FlightLogEntry, "arrival"> | null;
  simUsedGal?: number | null;
}): { gallons: number; source: DataSourceKind | null } {
  const picked = resolveTripFuelGal({
    userArrivalGal: arrivalFuelUsedGal(input.log),
    simUsedGal: input.simUsedGal,
    ofpPlannedGal: ofpPlannedFuelGal(input.mission?.ofpAttachment),
    deskEstimateGal: deskFuelEstimateGal(input.mission),
  });
  return { gallons: picked?.value ?? 0, source: picked?.source ?? null };
}
